import { Plus } from 'lucide-react';
import { useState } from 'react';

import { RoleEditor } from '../../components/admin/role-editor.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { keys, useApiMutation, useRoles, type Role } from '../../lib/admin-api.ts';
import { api, errorMessage } from '../../lib/api.ts';
import { useToast } from '../../lib/toast.tsx';

type Editing = { mode: 'create'; from?: Role } | { mode: 'edit'; role: Role };

export function RolesPage() {
  const roles = useRoles();
  const notify = useToast();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);
  const refresh = [keys.roles, keys.assignableRoles];
  const save = useApiMutation(
    (input: { id?: string; name: string; permissions: string[] }) =>
      input.id
        ? api(`/roles/${input.id}`, {
            method: 'PATCH',
            body: { name: input.name, permissions: input.permissions },
          })
        : api('/roles', {
            method: 'POST',
            body: { name: input.name, permissions: input.permissions },
          }),
    refresh,
  );
  const remove = useApiMutation((id: string) => api(`/roles/${id}`, { method: 'DELETE' }), refresh);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Roles y permisos</h1>
          <p className="text-sm text-muted-foreground">
            Qué puede hacer cada rol y sobre qué registros: los propios, los de su equipo o todos.
          </p>
        </div>
        <Button
          variant="brand"
          onClick={() => {
            save.reset();
            setEditing({ mode: 'create' });
          }}
        >
          <Plus /> Nuevo rol
        </Button>
      </div>

      {roles.isPending ? (
        <Loading />
      ) : roles.error ? (
        <Alert tone="danger">{errorMessage(roles.error)}</Alert>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
          {roles.data.map((role) => (
            <li
              key={role.id}
              className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:p-4"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {role.name} {role.baseKey && <Badge tone="info">Rol base</Badge>}
                </p>
                <p className="text-sm text-muted-foreground">
                  {role.memberCount === 1 ? '1 persona' : `${String(role.memberCount)} personas`} ·{' '}
                  {role.permissions.length} permisos
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    save.reset();
                    setEditing({ mode: 'edit', role });
                  }}
                >
                  Editar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    save.reset();
                    setEditing({ mode: 'create', from: role });
                  }}
                >
                  Duplicar
                </Button>
                {!role.baseKey && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      remove.reset();
                      setDeleting(role);
                    }}
                  >
                    Eliminar
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <Dialog
          open
          className="sm:w-[min(48rem,calc(100vw-2rem))]"
          title={editing.mode === 'edit' ? `Editar rol: ${editing.role.name}` : 'Nuevo rol'}
          onClose={() => {
            setEditing(null);
          }}
        >
          <RoleEditor
            initialName={
              editing.mode === 'edit'
                ? editing.role.name
                : editing.from
                  ? `${editing.from.name} (copia)`
                  : ''
            }
            initialGrants={
              editing.mode === 'edit' ? editing.role.permissions : (editing.from?.permissions ?? [])
            }
            saving={save.isPending}
            error={save.error ? errorMessage(save.error) : null}
            onCancel={() => {
              setEditing(null);
            }}
            onSave={(input) => {
              save.mutate(
                { ...input, ...(editing.mode === 'edit' ? { id: editing.role.id } : {}) },
                {
                  onSuccess: () => {
                    notify('Rol guardado.', 'success');
                    setEditing(null);
                  },
                },
              );
            }}
          />
        </Dialog>
      )}

      {deleting && (
        <Dialog
          open
          title={`Eliminar el rol ${deleting.name}`}
          description="Solo se pueden eliminar roles sin personas ni invitaciones pendientes."
          onClose={() => {
            setDeleting(null);
          }}
        >
          <div className="space-y-4">
            {remove.error && <Alert tone="danger">{errorMessage(remove.error)}</Alert>}
            <Button
              variant="danger"
              className="w-full"
              loading={remove.isPending}
              onClick={() => {
                remove.mutate(deleting.id, {
                  onSuccess: () => {
                    notify('Rol eliminado.', 'success');
                    setDeleting(null);
                  },
                });
              }}
            >
              Eliminar rol
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
